import React, { useEffect } from "react";
import { WifiSlash, ArrowsClockwise } from "@phosphor-icons/react";
import { brandLogoFull } from "../brand";
import { track } from "../api";
import { useConnectivity } from "../context/ConnectivityContext";

export default function OfflineScreen() {
  const { status, lastConnected, retry, isExpeditionActive } = useConnectivity();
  const reconnecting = status === "reconnecting";
  const paused = isExpeditionActive?.();

  useEffect(() => { track("offline_screen_viewed"); }, []);

  return (
    <div className="fixed inset-0 z-[9997] flex flex-col items-center justify-center bg-ff-bg px-6 text-center ff-grain" data-testid="offline-screen">
      <img src={brandLogoFull} alt="Aurumward" className="w-[min(70vw,300px)] object-contain" />
      {paused ? (
        <>
          <h1 className="mt-5 font-display text-2xl italic text-ff-gold sm:text-3xl" data-testid="offline-expedition-paused">Connection lost — expedition paused.</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-ff-parchment/80">
            New decisions and skill challenges are disabled while offline. Do not close this
            expedition if you want to resume from your last confirmed step — it is stored on the server.
          </p>
        </>
      ) : (
        <>
          <h1 className="mt-5 font-display text-2xl italic text-ff-gold sm:text-3xl">The trail has gone quiet.</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-ff-parchment/80">
            Aurumward cannot reach the server right now. Your installed app is still available,
            and your progress has not been altered.
          </p>
        </>
      )}

      <div className="mt-5 flex items-center gap-2 text-xs uppercase tracking-widest" data-testid="offline-status">
        <span className={`inline-block h-2 w-2 rounded-full ${reconnecting ? "bg-ff-gold animate-pulse" : "bg-ff-danger"}`} />
        <span className="text-ff-brass">{reconnecting ? "Reconnecting…" : "Offline"}</span>
      </div>
      {lastConnected && (
        <p className="mt-1 text-[11px] text-ff-muted" data-testid="offline-last-connected">
          Last connected {new Date(lastConnected).toLocaleTimeString()}
        </p>
      )}

      <div className="mt-7 flex w-[min(90vw,320px)] flex-col gap-3">
        <button onClick={retry} disabled={reconnecting} data-testid="offline-retry-btn"
          className="inline-flex items-center justify-center gap-2 rounded-sm bg-ff-gold px-5 py-3 text-sm font-semibold text-ff-bg transition-colors hover:bg-ff-brass disabled:opacity-50">
          <ArrowsClockwise size={18} weight="bold" className={reconnecting ? "animate-spin" : ""} /> Retry Connection
        </button>
        <a href="/" data-testid="offline-home-btn"
          className="inline-flex items-center justify-center gap-2 rounded-sm border border-ff-gold/50 px-5 py-3 text-sm font-semibold text-ff-gold transition-colors hover:bg-ff-gold/10">
          Return to Cached Home
        </a>
      </div>

      <p className="mt-6 flex max-w-sm items-center gap-2 text-[11px] text-ff-muted">
        <WifiSlash size={14} /> Server-controlled gameplay (stakes, decisions, outcomes and rewards)
        requires reconnection. Your last confirmed progress is stored safely on the server.
      </p>
    </div>
  );
}
